import type { IContractToRegistration } from '@klp/contractregistration/classes/IContractToRegistration';
import { Tooltip } from '@klp/design/Tooltip';
import { Typography } from '@klp/design/Typography/Typography';
import type { MUIDataTableColumn } from 'mui-datatables';
import React from 'react';
import i18n from '@forvalter/i18n/config';
import { FileDownload } from '@klp/filedownload/FileDownload';
import { contractToRegistrationApiRoutes } from '@klp/contractregistration/config';
import { Link } from '@klp/design/Link';
import { formatDate, parseSafeDate } from '@klp/utils/dateTimeFormatter';
import { getRegistreringStatusTranslations } from '@klp/contractregistration/utils';
import type { useKlpStyles } from '@klp/design/index';
import type { useHistory } from 'react-router-dom';
import { Actions } from './Actions';

type getTableColumnsProps = {
    registrationLines: Array<IContractToRegistration>;
    classes: ReturnType<typeof useKlpStyles>;
    history: ReturnType<typeof useHistory>;
    handleShareDocument: (
        dokumentId: string,
        event: React.MouseEvent<HTMLElement>,
    ) => void;
    handleClosePopper: () => void;
    handleTogglePopper: (
        id: string,
        event: React.MouseEvent<HTMLElement>,
    ) => void;
    openPopperId: string | null;
    anchorEl: HTMLElement | null;
};

export const getTableColumns = ({
    registrationLines,
    classes,
    history,
    handleShareDocument,
    handleClosePopper,
    handleTogglePopper,
    openPopperId,
    anchorEl,
}: getTableColumnsProps) => {
    const tableColumns: Array<MUIDataTableColumn> = [
        {
            label: i18n.t('contractRegistration.table.property'),
            name: 'byggNavn',
            options: {
                customBodyRender: (value?: string) => {
                    return (
                        <Tooltip
                            translation="contractRegistration.table.statusCode.noPropertyWarning"
                            title=""
                            hideTooltip={Boolean(value)}
                        >
                            <Typography
                                klpTypographyVariant="small"
                                klpColor="main"
                            >
                                {value || '-'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
        },
        {
            label: i18n.t('contractRegistration.table.tenant'),
            name: 'kundeNavn',
            options: {
                customBodyRender: (value: string | null) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: i18n.t('contractRegistration.table.contractDocument'),
            name: 'navn',
            options: {
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const line = registrationLines[dataIndex];
                    const fileId = line?.filMetadata?.id;
                    const fileName = line?.filMetadata?.name;
                    return fileId ? (
                        <div className={classes.download}>
                            <FileDownload
                                path={contractToRegistrationApiRoutes.download(
                                    fileId,
                                )}
                                name={fileName}
                                preview={true}
                                stopTableClickPropagation={true}
                            >
                                <Link
                                    klpTypographyVariant="small"
                                    klpColor="main"
                                    data-cy="ContractToRegistrationTable_kundeNavn"
                                >
                                    {line.navn}
                                </Link>
                            </FileDownload>
                        </div>
                    ) : null;
                },
            },
        },
        {
            label: i18n.t('contractRegistration.table.signDate'),
            name: 'signertDato',
            options: {
                customBodyRender: (value: string) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatDate(parseSafeDate(value))}
                    </Typography>
                ),
            },
        },
        {
            label: i18n.t('contractRegistration.table.propertyManager'),
            name: 'ansvarligNavn',
            options: {
                customBodyRender: (value: string | null) => {
                    return (
                        <Typography
                            klpTypographyVariant="small"
                            klpColor="main"
                        >
                            {value}
                        </Typography>
                    );
                },
            },
        },
        {
            label: i18n.t('contractRegistration.table.economyPerson'),
            name: 'ansvarligOekonomiNavn',
            options: {
                customBodyRender: (value: string | null) => {
                    return (
                        <Typography
                            klpTypographyVariant="small"
                            klpColor="main"
                        >
                            {value}
                        </Typography>
                    );
                },
            },
        },
        {
            label: i18n.t('contractRegistration.table.type'),
            name: 'registreringProsessType',
            options: {
                display: false,
                customBodyRender: (value) => (
                    <Typography
                        klpTypographyVariant="small"
                        klpColor="main"
                        translation={`contractRegistration.contractTypes.${value}`}
                    />
                ),
            },
        },
        {
            label: i18n.t('contractRegistration.table.statusCode.title'),
            name: 'registreringStatus',
            options: {
                sort: true,
                customBodyRender: (value) => {
                    const status = value as string;
                    return (
                        <Typography
                            klpTypographyVariant="small"
                            klpColor="main"
                        >
                            {i18n.t(getRegistreringStatusTranslations(status))}
                        </Typography>
                    );
                },
            },
        },
        {
            label: i18n.t('contractRegistration.table.action'),
            name: 'download',
            options: {
                sort: false,
                setCellHeaderProps: () => {
                    return { align: 'center' };
                },
                customBodyRenderLite: (dataIndex) => {
                    const line = registrationLines[dataIndex];
                    const dokumentId = line?.dokumentId;
                    const status = line?.registreringStatus;

                    return (
                        <Actions
                            line={line}
                            dokumentId={dokumentId}
                            status={status}
                            history={history}
                            handleShareDocument={handleShareDocument}
                            handleClosePopper={handleClosePopper}
                            handleTogglePopper={handleTogglePopper}
                            openPopperId={openPopperId}
                            anchorEl={anchorEl}
                        />
                    );
                },
            },
        },
    ];

    return tableColumns;
};
